import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchSettings = async (forceRefresh = false) => {
    // Return cached data if available and not expired
    if (!forceRefresh && settings && lastFetch && Date.now() - lastFetch < CACHE_DURATION) {
      return settings;
    }

    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(`${API_BASE_URL}/settings`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        setSettings(data.data);
        setLastFetch(Date.now());
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to fetch settings');
      }
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    setLastFetch(Date.now());
  };

  const refreshSettings = () => {
    return fetchSettings(true);
  };

  // Initial fetch
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await fetch(`${API_BASE_URL}/settings`);
        const data = await res.json();
        
        if (res.ok && data.success) {
          setSettings(data.data);
          setLastFetch(Date.now());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    initialLoad();
  }, []);

  const value = {
    settings,
    loading,
    error,
    fetchSettings,
    updateSettings,
    refreshSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
